import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Users, TrendingUp, MapPin, Award, Building2, ShoppingBag } from 'lucide-react';

const BrandLeasing = () => {
  const sectors = [
    { name: 'Food & Beverage', icon: '🍽️', count: '15+' },
    { name: 'Apparel & Fashion', icon: '👔', count: '12+' },
    { name: 'Hypermarkets', icon: '🛒', count: '8+' },
    { name: 'Hotels & Hospitality', icon: '🏨', count: '6+' },
    { name: 'Healthcare', icon: '🏥', count: '4+' },
    { name: 'Malls & Retail', icon: '🏬', count: '5+' }
  ];

  return (
    <div className="min-h-screen pt-20 lg:pt-24">
      <Helmet>
        <title>Brand Leasing & Consultancy | Arthaa Build — 10+ Years, 50+ Brand Partnerships</title>
        <meta name="description" content="Arthaa Build brand leasing consultancy — 10+ years connecting 50+ national and international brands with prime commercial real estate in Punjab, Haryana, Delhi NCR. Food, apparel, hospitality, retail partnerships." />
        <link rel="canonical" href="https://arthaabuild.com/brand-leasing" />
      </Helmet>

      {/* Header */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="text-sm uppercase tracking-widest text-[#7EB3D8] font-semibold mb-4">Arthaa Build — Brand Leasing & Consultancy</p>
            <h1 className="font-serif font-bold text-5xl sm:text-6xl mb-6">
              10 Years of Brand Partnerships
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              Connecting premium national and international brands with prime real estate across North India. Over 50+ successful partnerships in food, apparel, hospitality, and retail.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-[#7EB3D8] mb-1">10+</div>
                <div className="text-xs text-white/80">Years Experience</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-[#7EB3D8] mb-1">50+</div>
                <div className="text-xs text-white/80">Brand Partnerships</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-[#7EB3D8] mb-1">5 States</div>
                <div className="text-xs text-white/80">Coverage</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sectors */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="font-serif font-bold text-4xl text-slate-900 mb-4">Our Expertise Across Sectors</h2>
            <p className="text-lg text-slate-600">Proven track record with national and international brands</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {sectors.map((sector, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="luxury-card bg-gradient-to-br from-slate-50 to-white p-8 rounded-xl text-center"
              >
                <div className="text-5xl mb-4">{sector.icon}</div>
                <h3 className="font-serif font-bold text-xl text-slate-900 mb-2">{sector.name}</h3>
                <p className="text-[#2B5278] font-bold text-lg">{sector.count} Brands</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <h2 className="font-serif font-bold text-4xl text-slate-900 mb-12 text-center">Our Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="luxury-card bg-white p-8 rounded-xl">
              <Users className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-lg text-slate-900 mb-2">Brand Liaison</h3>
              <p className="text-sm text-slate-600">Connect brands with ideal locations</p>
            </div>
            <div className="luxury-card bg-white p-8 rounded-xl">
              <MapPin className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-lg text-slate-900 mb-2">Site Selection</h3>
              <p className="text-sm text-slate-600">Market analysis & location strategy</p>
            </div>
            <div className="luxury-card bg-white p-8 rounded-xl">
              <TrendingUp className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-lg text-slate-900 mb-2">Lease Negotiation</h3>
              <p className="text-sm text-slate-600">Commercial terms & agreements</p>
            </div>
            <div className="luxury-card bg-white p-8 rounded-xl">
              <Award className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-lg text-slate-900 mb-2">Market Research</h3>
              <p className="text-sm text-slate-600">Footfall, demographics, competition</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-20 text-center">
          <h2 className="font-serif font-bold text-4xl text-slate-900 mb-6">Pan-North India Coverage</h2>
          <p className="text-lg text-slate-600 mb-8">Active across major markets with deep local expertise</p>
          <div className="flex flex-wrap justify-center gap-4 text-slate-700 font-medium">
            {['Punjab', 'Haryana', 'Delhi NCR', 'Uttar Pradesh', 'Himachal Pradesh', 'Chandigarh'].map((state, i) => (
              <div key={i} className="px-6 py-3 bg-slate-100 rounded-lg">{state}</div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <h2 className="font-serif font-bold text-4xl mb-6">Looking for the Perfect Location?</h2>
          <p className="text-xl text-white/90 mb-8">Whether you're a brand seeking space or a developer seeking tenants—we connect the right partnerships.</p>
          <Link to="/contact" className="inline-block bg-white text-slate-900 hover:bg-slate-50 font-semibold px-10 py-4 rounded-md transition-all">
            Schedule Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BrandLeasing;